import "./ReactionMenu.css";

import { ElementType, FC, RefObject, useEffect, useRef, useState } from "react";

import { ReactionAnimation } from "components/reactions/ReactionAnimation";
import { VoteIndicator } from "components/reactions/VoteIndicator";
import { ReactionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { ControlledMenu, FocusableItem, SubMenu, useClick } from "mds/components/Menu";
import { VerticalDivider } from "mds/components/VerticalDivider";
import { ReactionIcon, ReactionVoteNoIcon, ReactionVoteYesIcon } from "mds/icons";
import { cn } from "mds/utils/cn";
import { useAppSelector } from "store";
import {
  selectCurrentClassUserId,
  selectCurrentClassUserReaction,
  selectCurrentClassUserVote,
} from "store/selectors";
import { useClassActions } from "utils/class-utils";
import {
  REACTION_ANIMATION_DURATION,
  REACTION_TO_COLOR_CLASSES,
  REACTION_TO_ICON,
  REACTION_TO_TITLE_KEY,
  REMOVE_REACTION_DELAY,
  VOTE_TO_COLOR_CLASSES,
} from "utils/reactions";

// ! prefix is needed to override squareLegacy button styles
const REACTION_TO_LARGE_BG_COLOR_CLASSES: Record<NonNullable<ReactionType>, string> = {
  like: "!bg-green",
  dislike: "!bg-yellow",
  agree: "!bg-orange",
  disagree: "!bg-black-tint-40",
  laugh: "!bg-[#8D386B]",
  wow: "!bg-blue",
  love: "!bg-[#F13359]",
  clap: "!bg-[#1596B4]",
};

const VOTE_TO_LARGE_BG_COLOR_CLASSES = {
  yes: "!bg-green",
  no: "!bg-red-tint-20",
};

type ReactionButtonProps = {
  className?: string;
  Icon: ElementType;
  isLarge?: boolean;
  isSelected?: boolean;
  title: string;
  onClick: () => void;
  eventName?: string;
};

const ReactionButton: FC<ReactionButtonProps> = ({
  className,
  Icon,
  isLarge = false,
  isSelected = false,
  title,
  onClick,
  eventName,
}) => {
  return (
    <Button
      className={cn("p-1.5", className)}
      eventName={eventName}
      isSelected={isSelected}
      title={title}
      onClick={onClick}
    >
      {isLarge ? <Icon height={36} width={36} /> : <Icon size="xxl" />}
    </Button>
  );
};

export const ReactionMenu: FC<{ isMobile?: boolean; isMenuItem?: boolean }> = ({
  isMobile = false,
  isMenuItem = false,
}) => {
  const useLargeLayout = isMobile || isMenuItem;
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const currentClassUserReaction = useAppSelector(selectCurrentClassUserReaction);
  const currentClassUserVote = useAppSelector(selectCurrentClassUserVote);
  const { toggleReaction, toggleVote } = useClassActions();
  const ref = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const anchorProps = useClick(isOpen, setIsOpen);
  const removeReactionTimeout = useRef<NodeJS.Timeout>(null);

  // Auto-clear reaction after animation is complete, plus a delay so the animation isn't cut short
  useEffect(() => {
    if (!currentClassUserReaction || !currentClassUserId) return;

    if (removeReactionTimeout.current) {
      clearTimeout(removeReactionTimeout.current);
    }

    removeReactionTimeout.current = setTimeout(() => {
      toggleReaction(null);
    }, REACTION_ANIMATION_DURATION + REMOVE_REACTION_DELAY);

    return () => {
      if (removeReactionTimeout.current) {
        clearTimeout(removeReactionTimeout.current);
      }
    };
  }, [currentClassUserReaction, currentClassUserId, toggleReaction]);

  // When isMobile, fold the active reaction/vote into the main button instead of rendering a
  // second button next to it. Reaction takes precedence over vote.
  const activeIndicator = currentClassUserReaction
    ? {
        Icon: REACTION_TO_ICON[currentClassUserReaction],
        bgClass: REACTION_TO_LARGE_BG_COLOR_CLASSES[currentClassUserReaction],
      }
    : currentClassUserVote
      ? {
          Icon: currentClassUserVote === "yes" ? ReactionVoteYesIcon : ReactionVoteNoIcon,
          bgClass: VOTE_TO_LARGE_BG_COLOR_CLASSES[currentClassUserVote],
        }
      : null;
  const showInlineIndicator = !isMobile && !!activeIndicator;
  const showLargeIndicator = isMobile && !!activeIndicator;

  const renderMenuContent = (closeMenu: () => void) => (
    <div className="flex flex-wrap gap-1">
      {Object.entries(REACTION_TO_COLOR_CLASSES).map(([reaction, colorClasses]) => (
        <ReactionButton
          className={colorClasses}
          eventName={
            currentClassUserReaction === reaction
              ? "Reactions - Clear my reaction"
              : `Reactions - ${reaction}`
          }
          Icon={REACTION_TO_ICON[reaction as NonNullable<ReactionType>]}
          isLarge={useLargeLayout}
          isSelected={currentClassUserReaction === reaction}
          key={reaction}
          title={t(REACTION_TO_TITLE_KEY[reaction as NonNullable<ReactionType>])}
          onClick={() => {
            toggleReaction(reaction as ReactionType);
            closeMenu();
          }}
        />
      ))}
      {!useLargeLayout && <VerticalDivider className="m-1.5 h-6 border-black-tint-40" />}
      <ReactionButton
        className={VOTE_TO_COLOR_CLASSES.no}
        eventName={
          currentClassUserVote === "no" ? "Reactions - Clear my vote" : "Reactions - Vote No"
        }
        Icon={ReactionVoteNoIcon}
        isLarge={useLargeLayout}
        isSelected={currentClassUserVote === "no"}
        title={t("reactions.vote_no")}
        onClick={() => {
          toggleVote("no");
          closeMenu();
        }}
      />
      <ReactionButton
        className={VOTE_TO_COLOR_CLASSES.yes}
        eventName={
          currentClassUserVote === "yes" ? "Reactions - Clear my vote" : "Reactions - Vote Yes"
        }
        Icon={ReactionVoteYesIcon}
        isLarge={useLargeLayout}
        isSelected={currentClassUserVote === "yes"}
        title={t("reactions.vote_yes")}
        onClick={() => {
          toggleVote("yes");
          closeMenu();
        }}
      />
    </div>
  );

  if (isMenuItem) {
    return (
      <SubMenu
        label={<IconText iconStart={<ReactionIcon />} text={t("reactions.reactions")} />}
        menuClassName="!w-[272px]"
        withArrow
      >
        <FocusableItem className="!h-auto !cursor-default !gap-0 !p-0 hover:!bg-transparent">
          {({ closeMenu }) => renderMenuContent(closeMenu)}
        </FocusableItem>
      </SubMenu>
    );
  }

  return (
    <>
      <div
        className={cn("reaction-menu flex items-center gap-0.5", {
          "rounded-lg border-2 border-black-tint-30 bg-black-tint-30": showInlineIndicator,
        })}
      >
        <Button
          className={cn(
            "relative",
            { "size-10 min-h-10 min-w-10": showInlineIndicator },
            showLargeIndicator && [activeIndicator.bgClass, "text-white"],
          )}
          eventName="Classroom Tools - Reactions menu opened"
          isSelected={isOpen}
          kind="squareLegacy"
          ref={ref}
          size={isMobile ? "xl" : "l"}
          title={t("reactions.reactions")}
          isSquare
          {...anchorProps}
        >
          {showLargeIndicator && currentClassUserId ? (
            <div className="large-indicator-container rounded-[inherit]">
              <VoteIndicator
                className="vote-indicator left-1/2 top-1/2 !size-auto -translate-x-1/2 -translate-y-1/2"
                classUserId={currentClassUserId}
                iconSizeProps={{ className: "!size-9", height: 36, width: 36 }}
              />
              <ReactionAnimation classUserId={currentClassUserId} />
            </div>
          ) : (
            <ReactionIcon size={isMobile ? "l" : "m"} />
          )}
        </Button>
        {showInlineIndicator && currentClassUserReaction && (
          <ReactionButton
            className={REACTION_TO_COLOR_CLASSES[currentClassUserReaction]}
            eventName="Reactions - Clear my reaction"
            Icon={REACTION_TO_ICON[currentClassUserReaction]}
            title={t(REACTION_TO_TITLE_KEY[currentClassUserReaction])}
            onClick={() => {
              toggleReaction(null);
            }}
          />
        )}
        {showInlineIndicator && !currentClassUserReaction && currentClassUserVote === "no" && (
          <ReactionButton
            className={cn("size-10", VOTE_TO_COLOR_CLASSES.no)}
            eventName="Reactions - Clear my vote"
            Icon={ReactionVoteNoIcon}
            title={t("reactions.vote_no")}
            onClick={() => {
              toggleVote("no");
            }}
          />
        )}
        {showInlineIndicator && !currentClassUserReaction && currentClassUserVote === "yes" && (
          <ReactionButton
            className={cn("size-10", VOTE_TO_COLOR_CLASSES.yes)}
            eventName="Reactions - Clear my vote"
            Icon={ReactionVoteYesIcon}
            title={t("reactions.vote_yes")}
            onClick={() => {
              toggleVote("yes");
            }}
          />
        )}
      </div>

      <ControlledMenu
        anchorRef={ref as RefObject<Element>}
        direction="top"
        menuClassName={useLargeLayout ? "!w-[272px]" : undefined}
        state={isOpen ? "open" : "closed"}
        data-dark
        onClose={() => setIsOpen(false)}
      >
        {renderMenuContent(() => setIsOpen(false))}
      </ControlledMenu>
    </>
  );
};
